import { memo, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tree } from 'react-arborist';
import type { NodeRendererProps } from 'react-arborist';
import {
  ChevronDown, ChevronRight, Plus, Pencil, Ban, RotateCcw, Move, Search, Repeat, Link2, Info,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { NodeTypeIconChip } from '@/components/NodeTypeIcon';
import { useAuthStore } from '@/stores/auth';
import type { NodeTreeItem, NodesResponse } from '@/stores/scope';

type SyncUserOrgMode = 'off' | 'overwrite' | 'append';
type SyncUserOrgStatus = { mode: SyncUserOrgMode; synced_user_count: number };

const syncModeCopy: Record<SyncUserOrgMode, { label: string; description: string }> = {
  off: { label: '連動しない', description: '社員側で所属を変更しても、連携userのアクセス権限は自動更新されません (デフォルト)' },
  overwrite: { label: '上書き連動', description: '社員の所属変更に合わせて、連携userの所属を旧→新に置換します (手動追加した割当は保持されます)' },
  append: { label: '追加連動', description: '社員の新しい所属を追加します。旧所属も90日間はそのまま保持され、その後自動的に解除されます' },
};

/** 「組織階層」タブ配下の社員→user所属連動セクション (docs/design/v03-user-employee-refinement.md §5-5)。
 *  company_admin限定 (バックエンド側もisCompanyAdminでゲート、§8-1参照)。 */
function OrgSyncSection() {
  const qc = useQueryClient();
  const isAdmin = useAuthStore((s) => s.user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false);
  const [confirmingForceSync, setConfirmingForceSync] = useState(false);

  const { data: status } = useQuery<SyncUserOrgStatus>({
    queryKey: ['tenant-features', 'sync-user-org'],
    queryFn: () => apiFetch<SyncUserOrgStatus>('/tenant-features/sync-user-org'),
    enabled: isAdmin,
  });

  const updateModeMut = useMutation({
    mutationFn: (mode: SyncUserOrgMode) => apiFetch('/tenant-features/sync-user-org', { method: 'PATCH', body: JSON.stringify({ mode }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-features', 'sync-user-org'] });
      toast.success('設定を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const forceSyncMut = useMutation({
    mutationFn: () => apiFetch<{ updated_user_ids: string[]; employees_processed: number }>('/tenant-features/sync-user-org/force-sync', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['tenant-features', 'sync-user-org'] });
      setConfirmingForceSync(false);
      toast.success(`${res.employees_processed}名の社員情報を元に、${res.updated_user_ids.length}件のユーザー所属を同期しました`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '強制同期に失敗しました'),
  });

  if (!isAdmin || !status) return null;

  return (
    <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Link2 size={16} className="text-chrome-base" />
        <h3 className="text-h3">社員の所属変更を連携ユーザーに自動反映する</h3>
      </div>
      <p className="text-sm text-neutral-sub">社員管理で所属店舗を変更した際、紐付くログインアカウントのアクセス権限をどう扱うかを設定します。</p>

      <fieldset className="space-y-2">
        <legend className="sr-only">連動モード</legend>
        {(Object.keys(syncModeCopy) as SyncUserOrgMode[]).map((mode) => (
          <label key={mode} className="flex items-start gap-2 text-sm p-2 rounded hover:bg-neutral-panel cursor-pointer">
            <input
              type="radio"
              name="sync-user-org-mode"
              className="mt-1"
              checked={status.mode === mode}
              disabled={updateModeMut.isPending}
              onChange={() => updateModeMut.mutate(mode)}
            />
            <span>
              <span className="font-medium">{syncModeCopy[mode].label}</span>
              {status.mode === mode && (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-chrome-base/10 text-chrome-base text-xs">現在の設定</span>
              )}
              <div className="text-xs text-neutral-sub mt-0.5">{syncModeCopy[mode].description}</div>
            </span>
          </label>
        ))}
      </fieldset>

      {status.mode !== 'off' && (
        <div className="flex items-start gap-2 p-3 rounded bg-chrome-base/5 border border-chrome-base/20 text-sm">
          <Info size={16} className="text-chrome-base shrink-0 mt-0.5" />
          <span>現在の連動対象ユーザー：<strong className="tabular-nums">{status.synced_user_count}名</strong>。次回の社員所属変更から自動反映されます。</span>
        </div>
      )}

      {status.mode === 'append' && (
        <p className="text-xs text-alert-text bg-alert-bg border border-alert-border/40 rounded p-2">
          追加連動は最小権限原則違反のリスクがあります。四半期に一度、連携ユーザーの所属棚卸を推奨します。
        </p>
      )}

      {status.mode !== 'off' && (
        <div>
          <button type="button" onClick={() => setConfirmingForceSync(true)}
            className="text-sm text-chrome-base hover:underline">
            今すぐ全社員の所属で強制同期する
          </button>
          <p className="text-xs text-neutral-sub mt-1">連動モードをOFFから切り替えた直後など、既存の乖離を今すぐ解消したい場合に使用します</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmingForceSync}
        title="全社員の所属で強制同期しますか？"
        confirmLabel="実行する"
        cancelLabel="キャンセル"
        loading={forceSyncMut.isPending}
        onClose={() => { if (!forceSyncMut.isPending) setConfirmingForceSync(false); }}
        onConfirm={() => forceSyncMut.mutate()}
        message={<p className="text-sm">テナント内の全社員について、現在の所属設定を連携ユーザーへ再度反映します。手動で追加した割当 (source=manual) には影響しません。</p>}
      />
    </div>
  );
}

/** `/settings/hierarchy` (組織階層タブ、v0.2.5 S-11、T-1103)
 *  設計: docs/design/v025-config-ui.md §2-1, §3-1, §3-2。
 *  操作種別: 誤字修正/名称変更 (PATCH /nodes/:id)、移動 (PATCH /nodes/:id/parent、モーダル方式)、
 *  停止 (DELETE)、再開 (POST reopen)、リニューアル (POST renew)。
 *
 *  設計からの意図的な乖離: §3-1 は「移動」を drill-down モーダルで選ばせる仕様だが、本実装では
 *  対象ノード自身とその子孫を除外したフラットな <select> (パス文字列表示) を採用した。
 *  循環は最終的にサーバー側 (§6.4) が検証するため安全性は保たれるが、UX としては簡略化している
 *  (v0.3 で drill-down UI へ拡張余地を残す、CLAUDE.md §6d)。また If-Match による楽観ロックは
 *  ヘッダ省略時オプトアウトの仕様のため本 UI からは送出していない (同時編集の希少性を踏まえた判断)。 */

type NodeType = { node_type_id: string; code: string; label: string; depth_level: number };
type IndustryTemplate = { template_id: string; template_name: string };

type FlatNode = { node: NodeTreeItem; path: string; depth: number };

const flattenTree = (roots: NodeTreeItem[]): FlatNode[] => {
  const result: FlatNode[] = [];
  const walk = (nodes: NodeTreeItem[], pathPrefix: string, depth: number) => {
    for (const n of nodes) {
      const path = pathPrefix ? `${pathPrefix} / ${n.name}` : n.name;
      result.push({ node: n, path, depth });
      walk(n.children, path, depth + 1);
    }
  };
  walk(roots, '', 0);
  return result;
};

/** 対象ノード自身とその子孫の node_id 集合 (移動先候補から除外するため) */
const collectSubtreeIds = (node: NodeTreeItem): Set<string> => {
  const ids = new Set<string>([node.node_id]);
  const walk = (n: NodeTreeItem) => {
    for (const c of n.children) { ids.add(c.node_id); walk(c); }
  };
  walk(node);
  return ids;
};

type HierarchyRowProps = {
  node: NodeRendererProps<NodeTreeItem>['node'];
  style: NodeRendererProps<NodeTreeItem>['style'];
  onEdit: (item: NodeTreeItem) => void;
  onAddChild: (item: NodeTreeItem) => void;
  onMove: (item: NodeTreeItem) => void;
  onRenew: (item: NodeTreeItem) => void;
  onStop: (item: NodeTreeItem) => void;
  onReopen: (nodeId: string) => void;
  reopenPending: boolean;
};

/** モジュールスコープで1度だけ定義し React.memo でラップする (StoreMultiSelect/NodeSwitcher/
 *  NodeMultiSelect と同型の不具合の横展開修正、2026-08-11)。内部定義のままだと再レンダーのたびに
 *  別コンポーネント扱いになり、react-arborist が可視行を毎回アンマウント/リマウントしてしまう。 */
const HierarchyRow = memo(function HierarchyRow({
  node, style, onEdit, onAddChild, onMove, onRenew, onStop, onReopen, reopenPending,
}: HierarchyRowProps) {
  const item = node.data;
  const isClosed = item.status !== 'active';
  return (
    <div style={style} className={`flex items-center gap-1 px-1 rounded group ${isClosed ? 'opacity-60' : ''}`}>
      {node.isInternal ? (
        <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          className="flex items-center justify-center w-5 h-5 text-neutral-sub shrink-0" aria-label={node.isOpen ? '折り畳む' : '開く'}>
          {node.isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : <span className="w-5 shrink-0" />}
      <NodeTypeIconChip icon={item.icon} depth={node.level} dimmed={isClosed} />
      <span className={`text-sm truncate ${isClosed ? 'line-through text-neutral-sub' : ''}`}>{item.name}</span>
      <span className="text-[11px] text-neutral-sub shrink-0">{item.label}</span>
      <span className="text-[11px] font-mono text-neutral-sub shrink-0">{item.node_code}</span>
      {isClosed ? (
        <span className="flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded bg-neutral-line text-neutral-sub shrink-0">
          <Ban size={10} aria-hidden="true" /> 停止中
        </span>
      ) : (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 shrink-0">稼働中</span>
      )}
      <span className="flex-1" />
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        {!isClosed && (
          <>
            <button type="button" aria-label="編集" title="編集"
              onClick={() => onEdit(item)}
              className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={13} /></button>
            <button type="button" aria-label="子ノードを追加" title="子ノードを追加"
              onClick={() => onAddChild(item)}
              className="p-1 text-brand-red hover:text-brand-red-hover"><Plus size={13} /></button>
            <button type="button" aria-label="移動" title="移動" onClick={() => onMove(item)}
              className="p-1 text-neutral-sub hover:text-neutral-text"><Move size={13} /></button>
            <button type="button" aria-label="リニューアル" title="リニューアル (別ノード扱いで作り直す)"
              onClick={() => onRenew(item)}
              className="p-1 text-neutral-sub hover:text-neutral-text"><Repeat size={13} /></button>
            <button type="button" aria-label="稼働終了日を設定" title="稼働終了日を設定 (停止)" onClick={() => onStop(item)}
              className="p-1 text-neutral-sub hover:text-warning-text"><Ban size={13} /></button>
          </>
        )}
        {isClosed && (
          <button type="button" aria-label="再開" title="再開" onClick={() => onReopen(item.node_id)}
            disabled={reopenPending}
            className="p-1 text-neutral-sub hover:text-brand-red disabled:opacity-40"><RotateCcw size={13} /></button>
        )}
      </div>
    </div>
  );
});

export function HierarchyPage() {
  const qc = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [query, setQuery] = useState('');

  const { data, isLoading } = useQuery<NodesResponse>({
    queryKey: ['nodes', 'hierarchy', showClosed],
    queryFn: () => apiFetch<NodesResponse>(`/nodes${showClosed ? '?include_closed=1' : ''}`),
  });
  const { data: nodeTypes = [] } = useQuery<NodeType[]>({
    queryKey: ['node-types'],
    queryFn: () => apiFetch<NodeType[]>('/node-types'),
  });
  // v0.3 業態初期表示自動選択 (migration 0033): 編集モーダルの業態プルダウン用
  const { data: industryTemplates = [] } = useQuery<IndustryTemplate[]>({
    queryKey: ['menu-dev-industry-templates'],
    queryFn: () => apiFetch<IndustryTemplate[]>('/menu-dev/industry-templates'),
    staleTime: Infinity,
  });

  const roots = data?.roots ?? [];
  const flat = useMemo(() => flattenTree(roots), [roots]);
  const nodeById = useMemo(() => new Map(flat.map((f) => [f.node.node_id, f.node])), [flat]);

  /** startParentId から祖先方向に業態が設定されているノードを探す (継承先プレビュー用) */
  const resolveInheritedIndustry = (startParentId: string | null): string | null => {
    let current = startParentId;
    while (current) {
      const n = nodeById.get(current);
      if (!n) return null;
      if (n.industry_template_id) return n.industry_template_id;
      current = n.parent_node_id;
    }
    return null;
  };

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['nodes'] });
    // v0.3 (user Q5確定): AIメニュー開発の新規セッション業態自動選択に即時反映する
    qc.invalidateQueries({ queryKey: ['ai-menu-default-industry'] });
  };

  // --- 作成/編集 (骨格) ---------------------------------------------------
  const [editing, setEditing] = useState<{
    mode: 'create' | 'edit';
    node_id?: string;
    parent_node_id: string | null;
    node_code: string;
    node_type_code: string;
    node_name: string;
    effective_from: string;
    operation_type: 'correction' | 'continue';
    // v0.3 業態初期表示自動選択 (migration 0033): '' = (継承する)、非空文字列 = 明示指定
    industry_template_id: string;
    // v0.4 追加7要件⑦ (D-237): 店舗間移動時間の haversine 概算に使う緯度経度 (任意入力)。
    // '' = 未入力のまま (null で送信)
    latitude: string;
    longitude: string;
  } | null>(null);

  const createMut = useMutation({
    mutationFn: (body: {
      node_code: string; node_type_code: string; parent_node_id: string | null; node_name: string;
      effective_from: string; industry_template_id: string | null; latitude: number | null; longitude: number | null;
    }) =>
      apiFetch('/nodes', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { invalidateAll(); setEditing(null); toast.success('ノードを作成しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '作成に失敗しました'),
  });

  const patchMut = useMutation({
    mutationFn: (v: {
      node_id: string; node_name: string; operation_type: 'correction' | 'continue'; effective_from: string;
      industry_template_id: string | null; latitude: number | null; longitude: number | null;
    }) =>
      apiFetch(`/nodes/${v.node_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          node_name: v.node_name, operation_type: v.operation_type, effective_from: v.effective_from,
          industry_template_id: v.industry_template_id, latitude: v.latitude, longitude: v.longitude,
        }),
      }),
    onSuccess: () => { invalidateAll(); setEditing(null); toast.success('ノードを更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  // --- 移動 ------------------------------------------------------------
  const [moving, setMoving] = useState<NodeTreeItem | null>(null);
  const [moveTarget, setMoveTarget] = useState<string>('');
  const moveMut = useMutation({
    mutationFn: (v: { node_id: string; new_parent_node_id: string | null }) =>
      apiFetch(`/nodes/${v.node_id}/parent`, { method: 'PATCH', body: JSON.stringify({ new_parent_node_id: v.new_parent_node_id }) }),
    onSuccess: () => { invalidateAll(); setMoving(null); toast.success('ノードを移動しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '移動に失敗しました (循環参照などの制約に違反している可能性があります)'),
  });

  // --- 停止/再開 ---------------------------------------------------------
  const [stopping, setStopping] = useState<NodeTreeItem | null>(null);
  const stopMut = useMutation({
    mutationFn: (nodeId: string) => apiFetch(`/nodes/${nodeId}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateAll(); setStopping(null); toast.success('ノードの稼働を終了しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '停止に失敗しました'),
  });
  const reopenMut = useMutation({
    mutationFn: (nodeId: string) => apiFetch(`/nodes/${nodeId}/reopen`, { method: 'POST' }),
    onSuccess: () => { invalidateAll(); toast.success('ノードを再開しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '再開に失敗しました'),
  });

  // --- リニューアル --------------------------------------------------------
  const [renewing, setRenewing] = useState<{ node_id: string; node_code: string; node_name: string } | null>(null);
  const renewMut = useMutation({
    mutationFn: (v: { node_id: string; node_code: string; node_name: string }) =>
      apiFetch(`/nodes/${v.node_id}/renew`, { method: 'POST', body: JSON.stringify({ node_code: v.node_code, node_name: v.node_name }) }),
    onSuccess: () => { invalidateAll(); setRenewing(null); toast.success('ノードをリニューアルしました (新しいノードとして作成されました)'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'リニューアルに失敗しました'),
  });

  const moveCandidates = moving
    ? flat.filter((f) => !collectSubtreeIds(moving).has(f.node.node_id))
    : [];

  // v0.3 業態初期表示自動選択: 選択中のノード種別が会社ルート (depth_level=0) かどうか
  const editingDepthLevel = editing ? nodeTypes.find((t) => t.code === editing.node_type_code)?.depth_level : undefined;
  const editingIsCompanyRoot = editingDepthLevel === 0;
  const editingInheritedIndustryId = editing ? resolveInheritedIndustry(editing.parent_node_id) : null;
  const editingInheritedIndustryName = editingInheritedIndustryId
    ? industryTemplates.find((t) => t.template_id === editingInheritedIndustryId)?.template_name
    : null;

  // v0.4 追加7要件⑦ (D-237): 緯度経度は任意入力だが、入力した場合は実在する座標範囲であることを
  // 保存前に確認する (範囲外はバックエンドの zod でも弾かれるが、保存ボタンを disabled にして
  // エラーレスポンスを待たずにその場で気づけるようにする)
  const isValidLatLng = (value: string, min: number, max: number): boolean => {
    if (value.trim() === '') return true;
    const n = Number(value);
    return Number.isFinite(n) && n >= min && n <= max;
  };
  const latLngInvalid = !!editing
    && (!isValidLatLng(editing.latitude, -90, 90) || !isValidLatLng(editing.longitude, -180, 180));

  // Row を module scope の HierarchyRow (memo済み) に切り出したため、ここでは行操作の
  // コールバックのみ確定させる (react-arborist の children には安定した参照を渡す)
  const handleEdit = useCallback((item: NodeTreeItem) => {
    setEditing({
      mode: 'edit', node_id: item.node_id, parent_node_id: item.parent_node_id,
      node_code: item.node_code, node_type_code: item.node_type_code, node_name: item.name,
      effective_from: new Date().toISOString().slice(0, 10), operation_type: 'continue',
      industry_template_id: item.industry_template_id ?? '',
      latitude: item.latitude !== null ? String(item.latitude) : '',
      longitude: item.longitude !== null ? String(item.longitude) : '',
    });
  }, []);
  const handleAddChild = useCallback((item: NodeTreeItem) => {
    setEditing({
      mode: 'create', parent_node_id: item.node_id, node_code: '', node_type_code: '',
      node_name: '', effective_from: new Date().toISOString().slice(0, 10), operation_type: 'continue',
      industry_template_id: '', latitude: '', longitude: '',
    });
  }, []);
  const handleMove = useCallback((item: NodeTreeItem) => {
    setMoving(item);
    setMoveTarget(item.parent_node_id ?? '');
  }, []);
  const handleRenew = useCallback((item: NodeTreeItem) => {
    setRenewing({ node_id: item.node_id, node_code: item.node_code, node_name: item.name });
  }, []);
  const handleStop = useCallback((item: NodeTreeItem) => setStopping(item), []);
  // reopenMut (useMutationの戻り値オブジェクト) は毎レンダー新規参照になるため依存配列に含めない。
  // mutate自体はTanStack Queryが安定参照を保証する (QA/design-and-codeレビュー指摘、2026-08-11、
  // 依存にreopenMut全体を含めると検索入力のたびに renderRow が再生成され memo 化が骨抜きになる)
  const handleReopen = useCallback((nodeId: string) => reopenMut.mutate(nodeId), [reopenMut.mutate]);

  const renderRow = useCallback(
    ({ node, style }: NodeRendererProps<NodeTreeItem>) => (
      <HierarchyRow
        node={node} style={style}
        onEdit={handleEdit} onAddChild={handleAddChild} onMove={handleMove}
        onRenew={handleRenew} onStop={handleStop} onReopen={handleReopen}
        reopenPending={reopenMut.isPending}
      />
    ),
    [handleEdit, handleAddChild, handleMove, handleRenew, handleStop, handleReopen, reopenMut.isPending],
  );

  // 検索1文字ごとの再走査で入力がカクつかないよう、絞り込み計算は低優先度に遅延させる
  // (React 18 useDeferredValue、StoreMultiSelect と同一パターン、2026-08-11)
  const deferredQuery = useDeferredValue(query);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">組織階層</h2>
          <p className="text-sm text-neutral-sub mt-1">会社・ブランド・エリア・店舗などの組織ツリーを管理します。</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({
            mode: 'create', parent_node_id: null, node_code: '', node_type_code: '',
            node_name: '', effective_from: new Date().toISOString().slice(0, 10), operation_type: 'continue',
            industry_template_id: '', latitude: '', longitude: '',
          })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap"
        >
          <Plus size={16} /> ノード追加
        </button>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="ノード名・コードで検索"
            className="pl-6 pr-2 py-1 border border-neutral-line rounded text-sm w-56" />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-neutral-sub">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          停止中も表示
        </label>
      </div>

      {/* モバイル(375px基準)対応: 完全な「フォーカスノードモード」(docs/design/v025-config-ui.md §5) は
          v0.3以降に見送り (docs/OPEN_ITEMS.md 記録済み、CLAUDE.md §6d 過剰実装回避)。本リリースでは
          最低限「横崩れせずスクロール可能」であることを保証する: 外側を overflow-x-auto にし、
          Tree 自体には行内容が潰れない最小幅を与えて横スクロールに逃がす (Design CRITICAL 対応)。 */}
      <div className="border border-neutral-line rounded-lg bg-white p-2 overflow-x-auto">
        {isLoading ? (
          <div className="p-4 text-neutral-sub text-sm">読み込み中…</div>
        ) : roots.length === 0 ? (
          <div className="p-6 text-center text-neutral-sub text-sm">
            まだノードがありません。「ノード追加」からルートノードを作成してください。
          </div>
        ) : (
          <div className="min-w-[640px]">
            <Tree<NodeTreeItem>
              data={roots}
              idAccessor="node_id"
              childrenAccessor="children"
              openByDefault
              searchTerm={deferredQuery}
              searchMatch={(node, term) => {
                const q = term.toLowerCase();
                return node.data.name.toLowerCase().includes(q) || node.data.node_code.toLowerCase().includes(q);
              }}
              width="100%"
              height={480}
              indent={20}
              rowHeight={32}
            >
              {renderRow}
            </Tree>
          </div>
        )}
      </div>

      <OrgSyncSection />

      {/* 作成/編集モーダル */}
      <Modal
        open={editing !== null}
        title={editing?.mode === 'create' ? 'ノード追加' : 'ノード編集'}
        onClose={() => setEditing(null)}
        size="sm"
        footer={editing && (
          <>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button
              type="button"
              disabled={
                createMut.isPending || patchMut.isPending || !editing.node_name.trim()
                || (editing.mode === 'create' && (!editing.node_code.trim() || !editing.node_type_code))
                || latLngInvalid
              }
              onClick={() => {
                const latitude = editing.latitude.trim() === '' ? null : Number(editing.latitude);
                const longitude = editing.longitude.trim() === '' ? null : Number(editing.longitude);
                if (editing.mode === 'create') {
                  createMut.mutate({
                    node_code: editing.node_code.trim(), node_type_code: editing.node_type_code,
                    parent_node_id: editing.parent_node_id, node_name: editing.node_name.trim(), effective_from: editing.effective_from,
                    industry_template_id: editing.industry_template_id || null,
                    latitude, longitude,
                  });
                } else {
                  patchMut.mutate({
                    node_id: editing.node_id!, node_name: editing.node_name.trim(),
                    operation_type: editing.operation_type, effective_from: editing.effective_from,
                    industry_template_id: editing.industry_template_id || null,
                    latitude, longitude,
                  });
                }
              }}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
            >
              {createMut.isPending || patchMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        )}
      >
        {editing && (
          <div className="space-y-3">
            {editing.mode === 'create' && (
              <>
                <div>
                  <label className="block text-sm text-neutral-sub mb-1">ノード種別*</label>
                  <select required value={editing.node_type_code}
                    onChange={(e) => {
                      const nextType = nodeTypes.find((t) => t.code === e.target.value);
                      setEditing({
                        ...editing, node_type_code: e.target.value,
                        // 会社ルートに切り替えたら業態選択は自動でクリアする (保存時の400を未然に防ぐ)
                        industry_template_id: nextType?.depth_level === 0 ? '' : editing.industry_template_id,
                      });
                    }}
                    className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                    <option value="">選択してください</option>
                    {nodeTypes.map((t) => <option key={t.node_type_id} value={t.code}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-neutral-sub mb-1">ノードコード*</label>
                  <input required value={editing.node_code} pattern="[A-Za-z0-9_-]+"
                    onChange={(e) => setEditing({ ...editing, node_code: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
                </div>
              </>
            )}
            {editing.mode === 'edit' && (
              <fieldset className="space-y-1">
                <legend className="text-sm text-neutral-sub mb-1">変更の種類</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="operation_type" checked={editing.operation_type === 'correction'}
                    onChange={() => setEditing({ ...editing, operation_type: 'correction' })} />
                  誤字修正 (現在の版を上書き、履歴に残しません)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="operation_type" checked={editing.operation_type === 'continue'}
                    onChange={() => setEditing({ ...editing, operation_type: 'continue' })} />
                  名称変更・組織変更 (新しい版を作成、履歴を残します)
                </label>
              </fieldset>
            )}
            <div>
              <label className="block text-sm text-neutral-sub mb-1">名称*</label>
              <input required value={editing.node_name}
                onChange={(e) => setEditing({ ...editing, node_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">業態 (任意)</label>
              <Select
                value={editing.industry_template_id || 'inherit'}
                disabled={editingIsCompanyRoot || industryTemplates.length === 0}
                onValueChange={(v) => setEditing({ ...editing, industry_template_id: v === 'inherit' ? '' : v })}
              >
                <SelectTrigger title={editingIsCompanyRoot ? '会社ノードには業態を設定できません' : undefined}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">
                    {editingInheritedIndustryName ? `(継承: ${editingInheritedIndustryName})` : '(継承する)'}
                  </SelectItem>
                  {industryTemplates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.template_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingIsCompanyRoot && (
                <p className="text-xs text-neutral-sub mt-1">会社ノードには業態を設定できません</p>
              )}
              {!editingIsCompanyRoot && industryTemplates.length === 0 && (
                <p className="text-xs text-neutral-sub mt-1">業態が未登録です</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">緯度・経度 (任意)</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text" inputMode="decimal" placeholder="緯度 (例: 35.6812)" value={editing.latitude}
                  aria-invalid={!isValidLatLng(editing.latitude, -90, 90)}
                  onChange={(e) => setEditing({ ...editing, latitude: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded border border-neutral-line focus-ring" />
                <input
                  type="text" inputMode="decimal" placeholder="経度 (例: 139.7671)" value={editing.longitude}
                  aria-invalid={!isValidLatLng(editing.longitude, -180, 180)}
                  onChange={(e) => setEditing({ ...editing, longitude: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <p className="text-xs text-neutral-sub mt-1">
                店舗間の移動時間の概算 (シフト管理) に使います。未入力の店舗は移動時間の判定対象外になります。
              </p>
              {latLngInvalid && (
                <p className="text-xs text-alert-text bg-alert-bg border border-alert-border/40 rounded p-2 mt-1">緯度は-90〜90、経度は-180〜180の範囲で入力してください。</p>
              )}
            </div>
            {(editing.mode === 'create' || editing.operation_type === 'continue') && (
              <div>
                <label className="block text-sm text-neutral-sub mb-1">稼働開始日*</label>
                <input required type="date" value={editing.effective_from}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 移動モーダル (docs/design/ui-guidelines.md §4 z-indexスケール: move-confirm=60。
          tree-popover(z-55)より確実に上に出す必要があるため、共有Modalの既定z-50から上書きする) */}
      <Modal
        open={moving !== null}
        title={`「${moving?.name ?? ''}」を移動`}
        onClose={() => { if (!moveMut.isPending) setMoving(null); }}
        size="sm"
        zIndexClass="z-[60]"
        footer={moving && (
          <>
            <button type="button" disabled={moveMut.isPending} onClick={() => setMoving(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel disabled:opacity-50">キャンセル</button>
            <button type="button" disabled={moveMut.isPending}
              onClick={() => moveMut.mutate({ node_id: moving.node_id, new_parent_node_id: moveTarget || null })}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {moveMut.isPending ? '移動中…' : 'この移動を実行する'}
            </button>
          </>
        )}
      >
        {moving && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">移動先の親ノード</label>
              <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                <option value="">(ルート直下)</option>
                {moveCandidates.map((f) => <option key={f.node.node_id} value={f.node.node_id}>{f.path}</option>)}
              </select>
            </div>
            <p className="text-xs text-neutral-sub">
              子ノードもすべて一緒に移動します。移動先が自分自身の子孫の場合はエラーになります。
            </p>
          </div>
        )}
      </Modal>

      {/* リニューアルモーダル */}
      <Modal
        open={renewing !== null}
        title="ノードをリニューアル"
        onClose={() => { if (!renewMut.isPending) setRenewing(null); }}
        size="sm"
        footer={renewing && (
          <>
            <button type="button" disabled={renewMut.isPending} onClick={() => setRenewing(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel disabled:opacity-50">キャンセル</button>
            <button type="button" disabled={renewMut.isPending || !renewing.node_code.trim() || !renewing.node_name.trim()}
              onClick={() => renewMut.mutate(renewing)}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {renewMut.isPending ? '実行中…' : 'リニューアルする'}
            </button>
          </>
        )}
      >
        {renewing && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-text">
              現在のノードは停止し、同じ親の配下に別ノードとして新規作成します (経営者交代・全面刷新等、別店舗扱いにしたい場合に使用)。
            </p>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">新しいノードコード*</label>
              <input required value={renewing.node_code} pattern="[A-Za-z0-9_-]+"
                onChange={(e) => setRenewing({ ...renewing, node_code: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">新しい名称*</label>
              <input required value={renewing.node_name}
                onChange={(e) => setRenewing({ ...renewing, node_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={stopping !== null}
        title={`「${stopping?.name ?? ''}」の稼働を終了しますか？`}
        destructive
        confirmLabel="それでも停止する"
        loading={stopMut.isPending}
        onClose={() => { if (!stopMut.isPending) setStopping(null); }}
        onConfirm={() => { if (stopping) stopMut.mutate(stopping.node_id); }}
        message={stopping && (
          <div className="space-y-2">
            <p className="text-sm">
              ノードの状態を<strong>「停止中」</strong>に変更します。DB からは削除されません
              (稼働終了日をセットするのみ)。過去データは残ります。
            </p>
            <p className="text-xs text-neutral-sub">
              子ノードは自動的には移動しません。必要な場合は先に「移動」で配下ノードを別の親に付け替えてください。
            </p>
          </div>
        )}
      />
    </div>
  );
}
